import { Fragment, type CSSProperties, type ReactNode } from "react";
import { AugmentRarity, AugmentTooltipEntry } from "@ugg/shared/hooks/aram-mayhem";
import { AUGMENT_RARITY_HEX, RARITY_LABEL } from "./augment-rarity";

// ─────────────────────────────────────────────────────────────────────
// ARAM Mayhem augment tooltip: name, rarity, and the augment's text.
//
// The text is League client markup (the same CDragon tag dialect as League
// Classic's tooltips), rendered by a small tag parser into React nodes rather
// than dangerouslySetInnerHTML, so it is SSR-safe and nothing in the remote
// file can inject markup.
//
// Differences from the Classic parser this is modelled on:
//   • Tag names can contain hyphens (<lol-uikit-tooltipped-keyword key='...'>).
//   • <li> is never closed in this data, so it renders a bullet and opens nothing.
//   • An unknown tag inherits its parent's style instead of resetting it, and a
//     close tag only closes its own matching open tag, so stray or unbalanced
//     tags can't restyle the rest of the text.
//   • %i:icon% inline icon markers are dropped; there are no icons for them.
// ─────────────────────────────────────────────────────────────────────

// Keys are lower case; tag names are matched case-insensitively because the
// data mixes cases (lifeSteal / lifesteal).
const TAG_STYLES: Record<string, CSSProperties> = {
  attention: { color: "#ffffff", fontWeight: 700 },
  status: { color: "#ffffff", fontWeight: 700 },
  b: { fontWeight: 700 },
  keyword: { color: "#f0e6d2" },
  keywordmajor: { color: "#f0e6d2", fontWeight: 700 },
  active: { color: "#f0e6d2", fontWeight: 700 },
  passive: { color: "#f0e6d2", fontWeight: 700 },
  recast: { color: "#f0e6d2", fontWeight: 700 },
  keywordstealth: { color: "#a497e0" },
  stealth: { color: "#a497e0" },
  spellname: { color: "#d5a353" },
  gold: { color: "#e0b354" },
  danger: { color: "#e0524f" },
  rules: { color: "#8b8ba7" },
  rule: { color: "#8b8ba7" },
  stattracking: { color: "#8b8ba7" },
  flavortext: { color: "#8b8ba7", fontStyle: "italic" },
  magicdamage: { color: "#31c8e6" },
  magic: { color: "#31c8e6" },
  scaleap: { color: "#31c8e6" },
  magicpen: { color: "#31c8e6" },
  scaleabilityhaste: { color: "#31c8e6" },
  physicaldamage: { color: "#ff9b6b" },
  physical: { color: "#ff9b6b" },
  scalead: { color: "#ff9b6b" },
  scaleaf: { color: "#ff9b6b" },
  armorpen: { color: "#ff9b6b" },
  scalelethality: { color: "#ff9b6b" },
  crit: { color: "#ff9b6b" },
  truedamage: { color: "#e0c56e" },
  onhit: { color: "#e0c56e" },
  scalehealth: { color: "#8fce5b" },
  health: { color: "#8fce5b" },
  scalebonus: { color: "#8fce5b" },
  scalemana: { color: "#5db6ff" },
  scalearmor: { color: "#f0b072" },
  scalemr: { color: "#8fd6ff" },
  healing: { color: "#4bd15a" },
  heal: { color: "#4bd15a" },
  scalehealing: { color: "#4bd15a" },
  lifesteal: { color: "#4bd15a" },
  omnivamp: { color: "#4bd15a" },
  shield: { color: "#c9c9d6" },
  speed: { color: "#e0a458" },
  ms: { color: "#e0a458" },
  attackspeed: { color: "#e0a458" },
};

// <font color='...'> is the only tag whose attribute matters. Spaces around
// "=" occur in the data (<font color = '#8347ff'>).
const FONT_TAG_COLOR = /\bcolor\s*=\s*(?:'([^']*)'|"([^"]*)"|([^\s'">]+))/i;

// Only #RGB, #RRGGBB or a bare CSS keyword may reach the style prop.
const SAFE_COLOR = /^(?:#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})|[a-zA-Z]+)$/;

function fontTagColor(attrs: string): string | undefined {
  const match = attrs.match(FONT_TAG_COLOR);
  const raw = (match ? match[1] ?? match[2] ?? match[3] ?? "" : "").trim();
  return raw && SAFE_COLOR.test(raw) ? raw : undefined;
}

const TOKEN_SPLIT = /(<br\s*\/?>|<\/?[a-zA-Z][\w-]*(?:\s[^<>]*?)?\s*\/?>|@[^@<>\s]+@|%i:[^%\s]+%)/g;
const OPEN_TAG = /^<([a-zA-Z][\w-]*)((?:\s[^<>]*?)?)\s*(\/?)>$/;
const CLOSE_TAG = /^<\/([a-zA-Z][\w-]*)\s*>$/;
const PLACEHOLDER = /^@([^@<>\s]+)@$/;
const PLACEHOLDER_GLOBAL = /@[^@<>\s]+@/g;
const TRAILING_BREAKS = /(?:\s*<br\s*\/?>)+\s*$/i;

const countPlaceholders = (text: string) => (text.match(PLACEHOLDER_GLOBAL) || []).length;

// Which of the two strings to show: the one with fewer unfilled @Token@
// values, preferring the detailed tooltip on a tie. In practice that is the
// tooltip when it is complete, otherwise the short description, which is
// usually complete when the tooltip isn't.
export function pickAugmentTooltipText(entry: AugmentTooltipEntry | undefined): string | undefined {
  if (!Array.isArray(entry)) return undefined;
  const [description, tooltip] = entry;
  const candidates = [tooltip, description].filter((text): text is string => typeof text === "string" && !!text.trim());
  if (candidates.length === 0) return undefined;
  return candidates.reduce((best, text) => (countPlaceholders(text) < countPlaceholders(best) ? text : best));
}

// Parse augment text into React nodes. `hasMissing` is true when any @Token@
// was replaced with "?", which drives the footnote.
export function renderAugmentText(text: string | undefined): { node: ReactNode; hasMissing: boolean } {
  if (typeof text !== "string" || !text) return { node: null, hasMissing: false };

  const tokens = text.replace(TRAILING_BREAKS, "").split(TOKEN_SPLIT);
  const stack: Array<{ name: string; style: CSSProperties | undefined }> = [];
  const nodes: ReactNode[] = [];
  let hasMissing = false;
  let key = 0;

  const currentStyle = () => stack[stack.length - 1]?.style;
  const pushText = (value: string) => {
    const style = currentStyle();
    nodes.push(
      style ? (
        <span key={key++} style={style}>
          {value}
        </span>
      ) : (
        <Fragment key={key++}>{value}</Fragment>
      ),
    );
  };

  for (const tok of tokens) {
    if (!tok) continue;

    if (/^<br\s*\/?>$/i.test(tok)) {
      nodes.push(<br key={key++} />);
      continue;
    }
    if (tok.startsWith("%i:")) {
      continue;
    }

    const open = tok.match(OPEN_TAG);
    if (open) {
      const [, rawName, attrs, selfClosing] = open;
      const name = rawName.toLowerCase();
      if (name === "li") {
        pushText("• ");
        continue;
      }
      if (selfClosing) continue;
      const own = name === "font" ? { color: fontTagColor(attrs) } : TAG_STYLES[name];
      const parent = currentStyle();
      stack.push({ name, style: own ? { ...parent, ...own } : parent });
      continue;
    }

    const close = tok.match(CLOSE_TAG);
    if (close) {
      const name = close[1].toLowerCase();
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].name === name) {
          stack.length = i;
          break;
        }
      }
      continue;
    }

    const placeholder = tok.match(PLACEHOLDER);
    if (placeholder) {
      // Text-append hook, not a value — render nothing.
      if (placeholder[1] === "SpellModifierDescriptionAppend") continue;
      hasMissing = true;
      pushText("?");
      continue;
    }

    pushText(tok);
  }

  return { node: <>{nodes}</>, hasMissing };
}

export function AugmentTooltipContent(props: { name: string; rarity: AugmentRarity; entry: AugmentTooltipEntry }) {
  const { name, rarity, entry } = props;
  const { node, hasMissing } = renderAugmentText(pickAugmentTooltipText(entry));

  return (
    <div className="max-w-[320px] text-[13px] leading-[18px]">
      <div className="font-bold text-accent-blue-400">{name}</div>
      <div className="mt-[2px] text-[12px]" style={{ color: AUGMENT_RARITY_HEX[rarity] }}>
        {`${RARITY_LABEL[rarity]} Augment`}
      </div>
      {node && <div className="mt-[6px] text-lavender-50">{node}</div>}
      {hasMissing && <div className="mt-[8px] text-[11px] text-[#ffa514]">"?" indicates a value that is only shown in game</div>}
    </div>
  );
}
